import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getOnboardingTracking,
  createOnboardingTracking,
  updateOnboardingTracking,
  OnboardingTrackingRecord,
  OnboardingTaskPayload,
  TaskStatus,
} from '@skygroup/shared/API/onboardingTrackingApi';

// ── Toggle to skip all API calls and use localStorage only ─────────
export const ONBOARDING_MOCK_MODE = false;
// ──────────────────────────────────────────────────────────────────

const DEFAULT_GROUP = 'new_user_onboarding_b2c';
// Fallback group used when the API-tracked group is disabled for this agent.
// Always uses localStorage (mock) regardless of ONBOARDING_MOCK_MODE.
export const FALLBACK_GROUP = 'onboarding';

export type B2cOnboardingStepType = 'do' | 'learn';

export interface OnboardingTaskDef {
  taskId: string;
  sortId: number;
  type: B2cOnboardingStepType;
  translationKey: string;
  estimatedTime: string;
  route: string;
}

interface OnboardingGroupConfig {
  tasks: OnboardingTaskDef[];
}

export interface OnboardingTrackingState {
  id: number | null;
  status: string | null;
  totalTasks: number;
  taskCompleted: number;
  startedAt: string | null;
  completedAt: string | null;
  taskStatuses: Record<string, TaskStatus>;
}

export interface OnboardingState {
  config: Record<string, OnboardingGroupConfig>;
  activeGroupTasksId: string;
  tracking: Record<string, OnboardingTrackingState>;
  initialized: Record<string, boolean>;
  isFetching: boolean;
}

// ── B2C step definitions ───────────────────────────────────────────
export const B2C_TASK_DEFS: OnboardingTaskDef[] = [
  { taskId: 'main_search',            sortId: 1,  type: 'learn', translationKey: 'step6',  estimatedTime: '2 min',  route: '/' },
  { taskId: 'packages_search',        sortId: 2,  type: 'do',    translationKey: 'step1',  estimatedTime: '3 min',  route: '/packages' },
  { taskId: 'dynamic_package_search', sortId: 3,  type: 'do',    translationKey: 'step7',  estimatedTime: '3 min',  route: '/dynamicPackageSearch' },
  { taskId: 'flights_search',         sortId: 4,  type: 'do',    translationKey: 'step2',  estimatedTime: '4 min',  route: '/flights' },
  { taskId: 'hotels_search',          sortId: 5,  type: 'do',    translationKey: 'step3',  estimatedTime: '3 min',  route: '/hotels' },
  { taskId: 'attractions_search',     sortId: 6,  type: 'do',    translationKey: 'step4',  estimatedTime: '3 min',  route: '/attractions' },
  { taskId: 'events_search',          sortId: 7,  type: 'do',    translationKey: 'step5',  estimatedTime: '3 min',  route: '/events' },
  { taskId: 'flight_reservation',     sortId: 8,  type: 'do',    translationKey: 'step8',  estimatedTime: '8 min',  route: '/flights' },
  { taskId: 'package_reservation',    sortId: 9,  type: 'do',    translationKey: 'step10', estimatedTime: '10 min', route: '/packages' },
  { taskId: 'explore_manage_order',   sortId: 10, type: 'learn', translationKey: 'step11', estimatedTime: '3 min',  route: '/order/manage' },
  { taskId: 'agent_banner_overview',  sortId: 11, type: 'learn', translationKey: 'step9',  estimatedTime: '3 min',  route: '/' },
];

function buildTrackingFromRecord(
  record: OnboardingTrackingRecord,
  configTasks: OnboardingTaskDef[]
): OnboardingTrackingState {
  const taskStatuses: Record<string, TaskStatus> = {};
  configTasks.forEach((t) => { taskStatuses[t.taskId] = 'not_started'; });
  record.tasks.forEach((t) => { taskStatuses[t.taskId] = t.status; });
  return {
    id: record.id,
    status: record.status,
    totalTasks: record.totalTasks,
    taskCompleted: record.taskCompleted,
    startedAt: record.startedAt,
    completedAt: record.completedAt,
    taskStatuses,
  };
}

const initialState: OnboardingState = {
  config: {
    [DEFAULT_GROUP]: { tasks: B2C_TASK_DEFS },
    [FALLBACK_GROUP]: { tasks: B2C_TASK_DEFS },
  },
  activeGroupTasksId: DEFAULT_GROUP,
  tracking: {},
  initialized: {},
  isFetching: false,
};

// ── initOnboarding thunk ───────────────────────────────────────────
export const initOnboarding = createAsyncThunk(
  'b2c_onboarding/init',
  async (groupTasksId: string, { getState }) => {
    const state = (getState() as any).onboarding as OnboardingState;
    if (state.initialized[groupTasksId]) return null;

    const configTasks = state.config[groupTasksId]?.tasks ?? [];

    // DEFAULT_GROUP (new_user_onboarding_b2c) API calls are temporarily disabled —
    // keep all tracking in localStorage until the API integration is re-enabled.
    if (ONBOARDING_MOCK_MODE || groupTasksId === FALLBACK_GROUP || groupTasksId === DEFAULT_GROUP) {
      const stored = localStorage.getItem(`onboarding_completed_${groupTasksId}`);
      const completedIds: string[] = stored ? JSON.parse(stored) : [];
      const taskStatuses: Record<string, TaskStatus> = {};
      configTasks.forEach((t) => {
        taskStatuses[t.taskId] = completedIds.includes(t.taskId) ? 'completed' : 'not_started';
      });
      return {
        groupTasksId,
        tracking: {
          id: null,
          status: 'started',
          totalTasks: configTasks.length,
          taskCompleted: completedIds.length,
          startedAt: null,
          completedAt: null,
          taskStatuses,
        } as OnboardingTrackingState,
      };
    }
    /* API branch — re-enable by removing `|| groupTasksId === DEFAULT_GROUP` above
    const records = await getOnboardingTracking(groupTasksId);

    if (!records.length) {
      // No server record yet → CREATE with all config tasks as not_started
      const tasks: OnboardingTaskPayload[] = configTasks.map((t) => ({
        taskId: t.taskId,
        status: 'not_started' as TaskStatus,
        sortId: t.sortId,
      }));
      const created = await createOnboardingTracking({ groupTasksId, tasks });
      return { groupTasksId, tracking: buildTrackingFromRecord(created, configTasks) };
    }

    const record = records[0];
    const configTaskIds = new Set(configTasks.map((t) => t.taskId));
    const serverTaskIds = new Set(record.tasks.map((t) => t.taskId));

    const hasAdditions = configTasks.some((t) => !serverTaskIds.has(t.taskId));
    const hasRemovals = record.tasks.some((t) => !configTaskIds.has(t.taskId));

    if (hasAdditions || hasRemovals) {
      // Config and server differ → CREATE: send current config tasks with server
      // statuses preserved for existing tasks; additions start as not_started,
      // tasks removed from config are simply excluded from the payload.
      const tasks: OnboardingTaskPayload[] = configTasks.map((t) => ({
        taskId: t.taskId,
        sortId: t.sortId,
        status: (record.tasks.find((st) => st.taskId === t.taskId)?.status ?? 'not_started') as TaskStatus,
      }));
      const created = await createOnboardingTracking({ groupTasksId, tasks });
      return { groupTasksId, tracking: buildTrackingFromRecord(created, configTasks) };
    }

    return { groupTasksId, tracking: buildTrackingFromRecord(record, configTasks) };
    */ // end API branch — remove the opening /* above to re-enable
  }
);

// ── markTaskDone thunk ─────────────────────────────────────────────
export const markTaskDone = createAsyncThunk(
  'b2c_onboarding/markTaskDone',
  async (taskId: string, { getState }) => {
    const state = (getState() as any).onboarding as OnboardingState;
    const groupTasksId = state.activeGroupTasksId;
    const configTasks = state.config[groupTasksId]?.tasks ?? [];
    const taskDef = configTasks.find((t) => t.taskId === taskId);
    if (!taskDef) return null;

    if (ONBOARDING_MOCK_MODE || groupTasksId === FALLBACK_GROUP || groupTasksId === DEFAULT_GROUP) {
      const key = `onboarding_completed_${groupTasksId}`;
      const stored = localStorage.getItem(key);
      const completedIds: string[] = stored ? JSON.parse(stored) : [];
      if (!completedIds.includes(taskId)) {
        localStorage.setItem(key, JSON.stringify([...completedIds, taskId]));
      }
      return { groupTasksId, taskId };
    }

    const tracking = state.tracking[groupTasksId];
    if (!tracking?.id) return { groupTasksId, taskId };

    const tasks: OnboardingTaskPayload[] = configTasks.map((t) => ({
      taskId: t.taskId,
      sortId: t.sortId,
      status: (t.taskId === taskId
        ? 'completed'
        : (tracking.taskStatuses[t.taskId] ?? 'not_started')) as TaskStatus,
    }));
    await updateOnboardingTracking(tracking.id, tasks);
    return { groupTasksId, taskId };
  }
);

// ── Slice ──────────────────────────────────────────────────────────
const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setActiveGroup(state, action: PayloadAction<string>) {
      state.activeGroupTasksId = action.payload;
    },
    resetInitialized(state, action: PayloadAction<string>) {
      delete state.initialized[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initOnboarding.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(initOnboarding.fulfilled, (state, action) => {
        state.isFetching = false;
        if (!action.payload) return;
        const { groupTasksId, tracking } = action.payload;
        state.tracking[groupTasksId] = tracking;
        state.initialized[groupTasksId] = true;
      })
      .addCase(initOnboarding.rejected, (state) => {
        state.isFetching = false;
      })
      .addCase(markTaskDone.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { groupTasksId, taskId } = action.payload;
        if (!state.tracking[groupTasksId]) return;
        const prev = state.tracking[groupTasksId].taskStatuses[taskId];
        if (prev !== 'completed') {
          state.tracking[groupTasksId].taskStatuses[taskId] = 'completed';
          state.tracking[groupTasksId].taskCompleted += 1;
        }
      });
  },
});

export const { setActiveGroup, resetInitialized } = onboardingSlice.actions;
export default onboardingSlice.reducer;
